import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@/lib/theme";
import { TOKEN_KEY } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  Activity, BarChart3, Boxes, ShoppingBag, Users, LayoutGrid,
  Sun, Moon, LogOut, ChevronRight, ChevronDown, Menu, X,
} from "lucide-react";
import { PeriodFilterBar } from "@/components/PeriodFilterBar";

const NAV = [
  { to: "/daily", label: "Daily Brief", icon: LayoutGrid, code: "01" },
  { to: "/pulse", label: "Pulse Feed", icon: Activity, code: "02" },
  {
    to: "/sales", label: "Sales Intelligence", icon: BarChart3, code: "03",
    children: [
      { to: "/sales/festivals", label: "Festivals" },
      { to: "/sales/departments", label: "Departments" },
    ],
  },
  {
    to: "/stock", label: "Stock Intelligence", icon: Boxes, code: "04",
    children: [
      { to: "/stock/overview", label: "Overview" },
      { to: "/stock/dead-stock", label: "Dead Stock Recovery" },
      { to: "/stock/vendors", label: "Vendor Scorecard" },
    ],
  },
  { to: "/procurement", label: "Procurement Intel", icon: ShoppingBag, code: "05" },
  {
    to: "/hr", label: "People Intelligence", icon: Users, code: "06",
    children: [
      { to: "/hr/overview", label: "Overview" },
      { to: "/hr/attendance", label: "Attendance" },
      { to: "/hr/attrition", label: "Attrition" },
      { to: "/hr/performance", label: "Performance Matrix" },
      { to: "/hr/demographics", label: "Demographics" },
      { to: "/hr/weekly-off", label: "Weekly Off Planner" },
    ],
  },
];

function NavList({ onNavigate }) {
  const location = useLocation();
  const [expanded, setExpanded] = useState(() => {
    // auto-expand the active section
    const cur = NAV.find((n) => location.pathname.startsWith(n.to));
    return cur ? { [cur.to]: true } : {};
  });

  return (
    <nav className="flex-1 py-3 overflow-y-auto">
      {NAV.map((item) => {
        const Icon = item.icon;
        const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
        const isOpen = expanded[item.to];
        const hasChildren = !!item.children;
        return (
          <div key={item.to} className="px-2">
            <div className="flex items-stretch">
              <NavLink
                to={item.to}
                onClick={() => {
                  if (hasChildren) setExpanded((s) => ({ ...s, [item.to]: !s[item.to] }));
                  if (!hasChildren) onNavigate?.();
                }}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex-1 flex items-center gap-3 px-3 py-3 text-[13px] font-sans transition-colors ${
                  active
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                }`}
              >
                <span className="font-mono text-[10px] text-muted-foreground w-5">{item.code}</span>
                <Icon className="w-4 h-4" strokeWidth={1.6} />
                <span className="flex-1">{item.label}</span>
                {hasChildren && (isOpen ? <ChevronDown className="w-3.5 h-3.5 opacity-60" /> : <ChevronRight className="w-3.5 h-3.5 opacity-60" />)}
              </NavLink>
            </div>
            {hasChildren && (isOpen || active) && (
              <div className="ml-10 pl-3 border-l border-border my-1">
                {item.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    onClick={onNavigate}
                    data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-2 text-[12px] ${
                        isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                      }`
                    }
                  >
                    {c.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function SidebarBrand() {
  return (
    <Link to="/daily" className="flex items-center gap-3 px-5 py-5 hairline-b" data-testid="sidebar-logo">
      <Logo size={36} className="text-foreground shrink-0" />
      <div>
        <div className="font-display text-xl tracking-tight leading-none">Mativex</div>
        <div className="label-caps mt-1">Retail Intelligence</div>
      </div>
    </Link>
  );
}

function DesktopSidebar() {
  return (
    <aside data-testid="sidebar" className="hidden md:flex flex-col w-[244px] shrink-0 hairline-r bg-background min-h-screen">
      <SidebarBrand />
      <NavList />
      <div className="hairline-t px-4 py-3 label-caps">v1.0 · IST · ₹ INR</div>
    </aside>
  );
}

function MobileDrawer({ open, onClose }) {
  // Lock scroll when open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`md:hidden fixed inset-0 z-40 bg-background/80 backdrop-blur-sm transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        data-testid="mobile-sidebar"
        className={`md:hidden fixed top-0 left-0 bottom-0 z-50 w-[280px] max-w-[85vw] bg-background hairline-r flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between hairline-b px-4 py-4">
          <Link to="/daily" onClick={onClose} className="flex items-center gap-3" data-testid="mobile-sidebar-logo">
            <Logo size={32} className="text-foreground" />
            <div>
              <div className="font-display text-lg leading-none">Mativex</div>
              <div className="label-caps mt-1">Retail Intelligence</div>
            </div>
          </Link>
          <button
            onClick={onClose}
            data-testid="mobile-drawer-close"
            className="w-9 h-9 hairline grid place-items-center"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <NavList onNavigate={onClose} />
        <div className="hairline-t px-4 py-3 label-caps">v1.0 · IST · ₹ INR</div>
      </aside>
    </>
  );
}

function TopBar({ onMenuOpen }) {
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const location = useLocation();
  // Hide period selector on pages where it doesn't apply
  const NO_PERIOD = ["/pulse", "/sales/festivals", "/stock/overview", "/stock/dead-stock", "/stock/vendors", "/hr/demographics", "/hr/weekly-off"];
  const showPeriod = !NO_PERIOD.some((p) => location.pathname.startsWith(p));
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/login");
  };
  return (
    <header className="hairline-b sticky top-0 z-30 bg-background">
      <div className="flex items-center gap-3 px-3 md:px-8 py-3">
        {/* Hamburger (mobile only) */}
        <button
          onClick={onMenuOpen}
          data-testid="mobile-menu-button"
          aria-label="Open menu"
          className="md:hidden w-10 h-10 hairline grid place-items-center"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Mobile mini-brand */}
        <div className="md:hidden flex items-center gap-2 min-w-0">
          <Logo size={26} className="text-foreground shrink-0" />
          <span className="font-display text-base truncate">Mativex</span>
        </div>

        {/* Desktop date */}
        <div className="hidden md:flex items-baseline gap-3">
          <span className="label-caps">Terminal</span>
          <span className="font-mono text-xs text-muted-foreground">
            {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
          </span>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {showPeriod && (
            <div className="hidden lg:block">
              <PeriodFilterBar />
            </div>
          )}
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            className="hairline w-10 h-10 grid place-items-center hover:bg-secondary transition-colors shrink-0"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="hairline px-3 h-10 text-xs uppercase tracking-widest hover:bg-secondary transition-colors flex items-center gap-2 shrink-0"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Logout</span>
          </button>
        </div>
      </div>

      {/* Period selector — second row on screens narrower than lg */}
      {showPeriod && (
        <div className="lg:hidden px-3 md:px-8 pb-3 -mt-1">
          <PeriodFilterBar />
        </div>
      )}
    </header>
  );
}

export default function Layout({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <DesktopSidebar />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar onMenuOpen={() => setDrawerOpen(true)} />
        <main className="flex-1 px-4 md:px-8 lg:px-12 py-5 md:py-8 pb-12">
          <div className="page-enter">{children}</div>
        </main>
      </div>
    </div>
  );
}
